/* ============================================================================
   Merchloop store request wizard, the catalog picker.

   Fetches the real SwagOS browse markup from templates/collection.mlsrpick.liquid
   and injects it, so facets, counts, sorting and paging are Shopify's own. Every
   control that would navigate is intercepted into a fetch.

   On top of that it adds the things a picker needs and a catalog page does not:
   browse-by tiles, pack shortcuts, a selection button on every card, and an
   in-frame product view instead of a trip to the product page.
   ========================================================================== */
(function () {
  'use strict';

  var PICK_VIEW = 'mlsrpick';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(cents) {
    var v = (cents || 0) / 100;
    return '$' + (v % 1 === 0 ? v.toFixed(0) : v.toFixed(2));
  }
  function cssEsc(s) { return (window.CSS && CSS.escape) ? CSS.escape(s) : s; }

  var ICO = {
    add: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>',
    tick: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
    eye: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>',
    x: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
    star: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>',
    // Intrinsic width and height on every icon, so a stale or missing
    // stylesheet cannot render one at 263px the way it once did.
    search: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>'
  };

  function Picker(host, api) {
    var self = this;
    this.host = host;
    this.api = api;
    this.byHandle = {};
    this.busy = false;
    this.seq = 0;

    host.innerHTML =
      '<div data-search></div>' +
      '<div data-tiles></div>' +
      '<div class="mlsr-cat__slot" data-slot></div>';
    this.search = host.querySelector('[data-search]');
    this.tiles = host.querySelector('[data-tiles]');
    this.slot = host.querySelector('[data-slot]');
    this.term = '';
    this.renderSearch();

    // Land on the best sellers rather than 1,286 products. Everything is one
    // click away, but a curated first screen is a far better place to start
    // than the whole catalog sorted alphabetically.
    this.load(
      api.popularTag
        ? api.baseUrl + '?view=' + PICK_VIEW + '&filter.p.tag=' + encodeURIComponent(api.popularTag)
        : api.baseUrl + '?view=' + PICK_VIEW,
      'replace');
    api.onSelectionChange(function () { self.paintAll(); });

    host.addEventListener('change', function (ev) {
      var form = self.slot.querySelector('[data-sw-facet-form]');
      if (form && ev.target.form === form) setTimeout(function () { self.refetch(); }, 0);
    });

    // Typing is debounced so a four letter word is one request, not four, and
    // the sequence guard in load() means a slow early response can never
    // overwrite a faster later one.
    var typeTimer = null;
    host.addEventListener('input', function (ev) {
      if (!ev.target.matches('[data-mlsr-q]')) return;
      var v = ev.target.value;
      clearTimeout(typeTimer);
      typeTimer = setTimeout(function () { self.runSearch(v); }, 280);
    });
    host.addEventListener('keydown', function (ev) {
      if (!ev.target.matches('[data-mlsr-q]')) return;
      if (ev.key === 'Enter') { ev.preventDefault(); clearTimeout(typeTimer); self.runSearch(ev.target.value); }
      if (ev.key === 'Escape' && ev.target.value) { ev.preventDefault(); self.clearSearch(); }
    });

    host.addEventListener('click', function (ev) {
      var pick = ev.target.closest('[data-mlsr-pick]');
      if (pick) {
        ev.preventDefault(); ev.stopPropagation();
        self.toggle(pick.getAttribute('data-mlsr-pick'));
        return;
      }
      var qv = ev.target.closest('[data-mlsr-qv]');
      if (qv) { ev.preventDefault(); ev.stopPropagation(); self.openQuickView(qv.getAttribute('data-mlsr-qv')); return; }

      if (ev.target.closest('[data-mlsr-clearsearch]')) {
        ev.preventDefault();
        self.clearSearch();
        return;
      }
      var tile = ev.target.closest('[data-mlsr-tile]');
      if (tile) {
        ev.preventDefault();
        // Picking a pack or a category is the opposite of searching, so the
        // box empties rather than leaving a term that no longer applies.
        self.term = '';
        self.renderSearch();
        self.load(tile.getAttribute('data-url'), 'replace');
        return;
      }
      var more = ev.target.closest('[data-mlsr-more]');
      if (more) { ev.preventDefault(); self.load(more.getAttribute('data-next'), 'append'); return; }

      var nav = ev.target.closest('[data-mlsr-nav]');
      if (nav) { ev.preventDefault(); self.load(nav.getAttribute('href'), 'replace'); return; }

      // A click anywhere else on a card opens the in-frame view rather than
      // leaving the wizard for the product page.
      var card = ev.target.closest('.sw-card');
      if (card) {
        ev.preventDefault();
        self.openQuickView(card.getAttribute('data-handle'));
        return;
      }
      var a = ev.target.closest('.sw-facets a[href], .sw-none a[href]');
      if (a && a.getAttribute('href').indexOf('/collections/') === 0) {
        ev.preventDefault();
        self.load(a.getAttribute('href'), 'replace');
      }
    });
  }

  /* -- loading -------------------------------------------------------------- */

  Picker.prototype.load = function (url, mode) {
    var self = this;
    if (mode === 'append' && this.busy) return;
    this.busy = true;
    var seq = ++this.seq;
    if (mode === 'replace') this.slot.classList.add('is-loading');

    var withView = url.indexOf('view=') === -1
      ? url + (url.indexOf('?') === -1 ? '?' : '&') + 'view=' + PICK_VIEW
      : url;

    fetch(withView, { credentials: 'same-origin', headers: { 'X-Requested-With': 'fetch' } })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (html) {
        if (seq !== self.seq) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var frag = doc.querySelector('[data-mlsr-cat]');
        if (!frag) throw new Error('no fragment');
        self.absorbData(doc);

        if (mode === 'append') {
          var newGrid = frag.querySelector('[data-sw-grid]');
          var grid = self.slot.querySelector('[data-sw-grid]');
          if (newGrid && grid) while (newGrid.firstElementChild) grid.appendChild(newGrid.firstElementChild);
          var oldWrap = self.slot.querySelector('.sw-loadwrap');
          var newWrap = frag.querySelector('.sw-loadwrap');
          if (oldWrap) { if (newWrap) oldWrap.replaceWith(newWrap); else oldWrap.remove(); }
        } else {
          self.slot.innerHTML = '';
          self.slot.appendChild(frag);
          self.currentUrl = withView;
          self.bindForm();
          self.renderTiles();
          var top = self.host.getBoundingClientRect().top + window.pageYOffset - 90;
          if (window.pageYOffset > top) window.scrollTo({ top: top, behavior: 'smooth' });
        }
        self.paintAll();
      })
      .catch(function () {
        if (seq !== self.seq) return;
        if (mode === 'replace') {
          self.slot.innerHTML = '<div class="mlsr-state"><h3>Couldn\'t load the catalog</h3>' +
            '<p>Check your connection and try again.</p>' +
            '<a class="mlsr-btn mlsr-btn--outline" href="' + esc(self.api.baseUrl) + '" data-mlsr-nav>Retry</a></div>';
        }
      })
      .then(function () {
        if (seq !== self.seq) return;
        self.busy = false;
        self.slot.classList.remove('is-loading');
      });
  };

  Picker.prototype.absorbData = function (doc) {
    var tag = doc.querySelector('[data-mlsr-catdata]');
    if (!tag) return;
    var data;
    try { data = JSON.parse(tag.textContent); } catch (e) { return; }
    var self = this;
    (data.products || []).forEach(function (p) { self.byHandle[p.h] = p; });
  };

  Picker.prototype.bindForm = function () {
    var self = this;
    var form = this.slot.querySelector('[data-sw-facet-form]');
    if (!form) return;
    form.submit = function () { self.refetch(); };
    form.addEventListener('submit', function (ev) { ev.preventDefault(); self.refetch(); });
  };
  Picker.prototype.refetch = function () {
    var form = this.slot.querySelector('[data-sw-facet-form]');
    if (form) this.load(this.serialize(form), 'replace');
  };
  Picker.prototype.serialize = function (form) {
    // The price control is a range slider resting at its own maximum, so a naive
    // FormData sends filter.v.price.lte=<cap> every time and silently hides
    // everything above it. A slider at max means no upper bound.
    var atMax = {};
    form.querySelectorAll('input[type="range"][name]').forEach(function (el) {
      if (el.max !== '' && el.value === el.max) atMax[el.name] = true;
    });
    var params = new URLSearchParams();
    new FormData(form).forEach(function (v, k) {
      if (v === '' || v == null || atMax[k]) return;
      params.append(k, v);
    });
    params.set('view', PICK_VIEW);
    return (form.getAttribute('action') || this.api.baseUrl) + '?' + params.toString();
  };

  /* -- search ----------------------------------------------------------------
     Browsing 1,292 products by facet is fine when you are exploring and useless
     when you already know you want a Carhartt beanie. Search goes through
     Shopify's own /search endpoint rather than a collection, because collection
     filters cannot do free text, which is why there is a second fragment
     template for it.

     Searching deliberately drops the active pack or filter and looks at the
     whole catalog. A search box that silently only searched inside whatever
     pack you happened to be on is how people conclude a product is not
     stocked. */

  Picker.prototype.renderSearch = function () {
    // Rebuilt rather than patched only when the term changes from the outside,
    // so typing is never interrupted by a re-render stealing the caret.
    var box = this.search.querySelector('[data-mlsr-q]');
    if (box) {
      if (box.value !== this.term) box.value = this.term;
      var clr = this.search.querySelector('[data-mlsr-clearsearch]');
      if (clr) clr.hidden = !this.term;
      return;
    }
    this.search.innerHTML =
      '<div class="mlsr-q">' +
        '<label class="mlsr-sr" for="mlsr-q">Search the catalog</label>' +
        '<span class="mlsr-q__ico">' + ICO.search + '</span>' +
        '<input class="mlsr-q__in" id="mlsr-q" type="search" autocomplete="off" ' +
          'placeholder="Search by product, brand or style number" data-mlsr-q ' +
          'value="' + esc(this.term) + '">' +
        '<button type="button" class="mlsr-q__x" data-mlsr-clearsearch aria-label="Clear search"' +
          (this.term ? '' : ' hidden') + '>' + ICO.x + '</button>' +
      '</div>';
  };

  // Shopify's /search looks at every product in the store, and this store also
  // holds thousands of products belonging to individual customer stores. An
  // unscoped search for "beanie" returned 1,000 results, most of them things
  // like "Kleinknecht NY - The North Face Circular Rib Beanie", which nobody
  // can order from this wizard and which carry no decoration spots. Scoping to
  // the catalog tag turns that into the 38 beanies we actually sell.
  var CATALOG_TAG = 'Catalog Products';

  Picker.prototype.runSearch = function (raw) {
    var term = String(raw == null ? '' : raw).trim();
    if (term === this.term) return;
    this.term = term;
    this.renderSearch();
    if (!term) return this.clearSearch();
    var scope = this.api.catalogTag || CATALOG_TAG;
    var q = term + ' tag:"' + scope + '"';
    this.load('/search?type=product&q=' + encodeURIComponent(q) + '&view=' + PICK_VIEW, 'replace');
  };

  Picker.prototype.clearSearch = function () {
    this.term = '';
    this.renderSearch();
    var back = this.api.popularTag
      ? this.api.baseUrl + '?view=' + PICK_VIEW + '&filter.p.tag=' + encodeURIComponent(this.api.popularTag)
      : this.api.baseUrl + '?view=' + PICK_VIEW;
    this.load(back, 'replace');
    var box = this.search.querySelector('[data-mlsr-q]');
    if (box) box.focus();
  };

  /* -- starting points ------------------------------------------------------- */

  // The theme's own facet rail already owns the taxonomy. It merges the raw tag
  // values into twelve clean categories, filters brands against a curated list,
  // and prints Shopify's real counts. This file used to re-derive both from the
  // raw checkboxes, which is why merchandising tags like Gifts and Outerwear
  // showed up as brands, and why twenty chips all read the same number: the
  // count was regexed off the end of the group, which is the "Show all 95"
  // button. One filtering system now, and it is the rail.
  //
  // What is left here is the merchandising the rail has no concept of: the full
  // catalog, the best sellers, and the curated packs. One row, never wrapping.
  Picker.prototype.renderTiles = function () {
    var base = this.api.baseUrl;
    var cur = this.currentUrl || '';
    var curPath = cur.split('?')[0];
    var pop = this.api.popularTag || '';
    var hasPop = !!pop && cur.indexOf('filter.p.tag=' + encodeURIComponent(pop)) !== -1;

    function tile(o) {
      return '<button type="button" class="mlsr-start' + (o.cls ? ' ' + o.cls : '') +
        (o.on ? ' is-on' : '') + '" data-mlsr-tile data-url="' + esc(o.url) + '"' +
        (o.on ? ' aria-current="true"' : '') + '>' +
        (o.img ? '<img class="mlsr-start__img" src="' + esc(o.img) + '" alt="" loading="lazy">' : '') +
        (o.icon || '') +
        '<span class="mlsr-start__l"><b>' + esc(o.label) + '</b>' +
        (o.note ? '<em>' + esc(o.note) + '</em>' : '') + '</span></button>';
    }

    var items = [tile({
      url: base + '?view=' + PICK_VIEW,
      label: 'Everything',
      note: 'Full catalog',
      cls: 'mlsr-start--all',
      on: curPath === base && !hasPop
    })];

    if (pop) {
      items.push(tile({
        url: base + '?view=' + PICK_VIEW + '&filter.p.tag=' + encodeURIComponent(pop),
        label: 'Most popular',
        note: 'Our best sellers',
        cls: 'mlsr-start--pop',
        icon: ICO.star,
        on: hasPop
      }));
    }

    (this.api.packs || []).forEach(function (p) {
      items.push(tile({
        url: '/collections/' + p.handle + '?view=' + PICK_VIEW,
        label: p.title,
        note: p.count ? p.count + ' items' : 'Curated pack',
        cls: 'mlsr-start--pack',
        img: p.image,
        on: curPath === '/collections/' + p.handle
      }));
    });

    this.tiles.innerHTML =
      '<div class="mlsr-starts">' +
        '<p class="mlsr-starts__t">Start from</p>' +
        '<div class="mlsr-starts__rail" data-mlsr-starts>' + items.join('') + '</div>' +
      '</div>';
  };

  /* -- selection and card chrome --------------------------------------------- */

  // How long the popup lingers after an add. Long enough to register that the
  // button confirmed, short enough that it does not read as a wait.
  var QV_CLOSE_MS = 420;

  Picker.prototype.toggle = function (handle) {
    var self = this;
    var p = this.byHandle[handle];
    if (!p) return;
    this.api.toggle(p);
    this.paint(handle);

    var modal = document.querySelector('[data-mlsr-qvmodal].is-open');
    if (!modal || modal.getAttribute('data-handle') !== handle) return;
    this.paintQuickView(handle);

    // Adding is the end of the errand, so the popup gets out of the way and
    // hands you straight back to the grid to pick the next thing. Removing
    // leaves it open, because that is a correction rather than a finish.
    if (!this.api.isSelected(handle)) return;
    clearTimeout(this.qvTimer);
    this.qvTimer = setTimeout(function () {
      var m = document.querySelector('[data-mlsr-qvmodal].is-open');
      if (m && m.getAttribute('data-handle') === handle) self.closeQuickView();
    }, QV_CLOSE_MS);
  };

  Picker.prototype.paintAll = function () {
    var self = this;
    this.slot.querySelectorAll('.sw-card[data-handle]').forEach(function (c) { self.decorate(c); });
  };
  Picker.prototype.paint = function (handle) {
    var card = this.slot.querySelector('.sw-card[data-handle="' + cssEsc(handle) + '"]');
    if (card) this.decorate(card);
  };

  Picker.prototype.decorate = function (card) {
    var handle = card.getAttribute('data-handle');
    var data = this.byHandle[handle] || {};
    var on = this.api.isSelected(handle);

    if (!card.__mlsrDone) {
      card.__mlsrDone = true;
      // The "ML" artwork chip is a catalog affordance with no meaning here.
      var mark = card.querySelector('.sw-artmark');
      if (mark) mark.remove();
      // The brand line falls back to the vendor when no real brand tag matches,
      // which renders a meaningless "Merchloop". Rebuild it from the sidecar.
      var cat = card.querySelector('.sw-ccat');
      if (cat) {
        var bits = [];
        if (data.brand && data.brand !== (this.api.vendorFallback || 'Merchloop')) bits.push(data.brand);
        if (data.cat) bits.push(data.cat);
        if (bits.length) cat.textContent = bits.join(' · ');
      }
      // Product links stay in the wizard; the card click opens the in-frame view.
      card.querySelectorAll('a[href^="/products/"]').forEach(function (a) {
        a.setAttribute('data-mlsr-qv', handle);
        a.removeAttribute('target');
      });
      var btnRow = document.createElement('div');
      btnRow.className = 'mlsr-cardact';
      btnRow.innerHTML =
        '<button type="button" class="mlsr-pick" data-mlsr-pick="' + esc(handle) + '"></button>' +
        '<button type="button" class="mlsr-look" data-mlsr-qv="' + esc(handle) + '" aria-label="View ' + esc(data.title || handle) + '">' + ICO.eye + '</button>';
      (card.querySelector('.sw-cbody') || card).appendChild(btnRow);
    }

    var btn = card.querySelector('[data-mlsr-pick]');
    if (btn) {
      btn.classList.toggle('is-on', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.innerHTML = on ? ICO.tick + '<span>Added</span>' : ICO.add + '<span>Add</span>';
    }
    card.classList.toggle('mlsr-on', on);
  };

  /* -- in-frame product view -------------------------------------------------- */

  Picker.prototype.modal = function () {
    var m = document.querySelector('[data-mlsr-qvmodal]');
    if (m) return m;
    m = document.createElement('div');
    m.className = 'mlsr-qv';
    m.setAttribute('data-mlsr-qvmodal', '');
    m.setAttribute('role', 'dialog');
    m.setAttribute('aria-modal', 'true');
    m.innerHTML = '<div class="mlsr-qv__scrim" data-mlsr-qvclose></div><div class="mlsr-qv__panel" data-body></div>';
    document.body.appendChild(m);
    var self = this;
    m.addEventListener('click', function (ev) {
      if (ev.target.closest('[data-mlsr-qvclose]')) return self.closeQuickView();
      var pick = ev.target.closest('[data-mlsr-pick]');
      if (pick) { self.toggle(pick.getAttribute('data-mlsr-pick')); return; }
      var sw = ev.target.closest('[data-qv-color]');
      if (sw) {
        m.querySelectorAll('[data-qv-color]').forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
        sw.setAttribute('aria-pressed', 'true');
        var img = m.querySelector('[data-qv-main]');
        if (img && sw.getAttribute('data-img')) img.src = sw.getAttribute('data-img');
        var nm = m.querySelector('[data-qv-colorname]');
        if (nm) nm.textContent = sw.getAttribute('data-qv-color');
      }
      var th = ev.target.closest('[data-qv-thumb]');
      if (th) {
        var main = m.querySelector('[data-qv-main]');
        if (main) main.src = th.getAttribute('data-qv-thumb');
      }
    });
    document.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape') self.closeQuickView();
    });
    return m;
  };

  Picker.prototype.openQuickView = function (handle) {
    var self = this;
    // Cancel a pending auto close, or reopening the same product inside the
    // grace window would shut again under the customer.
    clearTimeout(this.qvTimer);
    var m = this.modal();
    m.setAttribute('data-handle', handle);
    m.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    m.querySelector('[data-body]').innerHTML =
      '<div class="mlsr-qv__loading"><span class="mlsr-spin"></span></div>';

    var cached = this.qvCache && this.qvCache[handle];
    if (cached) return this.renderQuickView(cached);
    fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (p) {
        self.qvCache = self.qvCache || {};
        self.qvCache[handle] = p;
        if (m.getAttribute('data-handle') === handle) self.renderQuickView(p);
      })
      .catch(function () {
        var d = self.byHandle[handle] || {};
        self.renderQuickView({
          handle: handle, title: d.title || handle, price: Math.round((d.price || 0) * 100),
          images: d.img ? [d.img] : [], options: [], variants: [], description: ''
        });
      });
  };

  Picker.prototype.closeQuickView = function () {
    clearTimeout(this.qvTimer);
    var m = document.querySelector('[data-mlsr-qvmodal]');
    if (!m) return;
    m.classList.remove('is-open');
    m.removeAttribute('data-handle');
    document.body.style.overflow = '';
  };

  Picker.prototype.renderQuickView = function (p) {
    var m = this.modal();
    var side = this.byHandle[p.handle] || {};
    var colors = [], sizes = [];
    (p.options || []).forEach(function (o) {
      var nm = (o.name || '').toLowerCase();
      var vals = o.values || [];
      if (/colou?r|colorway/.test(nm)) colors = vals;
      else if (/size/.test(nm)) sizes = vals;
    });
    if (!colors.length && side.colors) colors = side.colors;

    var imgs = (p.images || []).slice(0, 6);
    var main = imgs[0] || side.img || '';
    var byColor = {};
    (p.variants || []).forEach(function (v) {
      if (v.featured_image && v.option1 && !byColor[v.option1]) byColor[v.option1] = v.featured_image.src;
    });

    var desc = String(p.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (desc.length > 420) desc = desc.slice(0, 420).replace(/\s+\S*$/, '') + '…';

    var on = this.api.isSelected(p.handle);

    m.querySelector('[data-body]').innerHTML =
      '<button type="button" class="mlsr-qv__x" data-mlsr-qvclose aria-label="Close">' + ICO.x + '</button>' +
      '<div class="mlsr-qv__grid">' +
        '<div class="mlsr-qv__media">' +
          '<div class="mlsr-qv__shot"><img data-qv-main src="' + esc(main) + '" alt="' + esc(p.title) + '"></div>' +
          (imgs.length > 1 ? '<div class="mlsr-qv__thumbs">' + imgs.map(function (src) {
            return '<button type="button" class="mlsr-qv__thumb" data-qv-thumb="' + esc(src) + '"><img src="' + esc(src) + '" alt=""></button>';
          }).join('') + '</div>' : '') +
        '</div>' +
        '<div class="mlsr-qv__info">' +
          '<p class="mlsr-qv__eyebrow">' +
            esc([side.brand && side.brand !== 'Merchloop' ? side.brand : '', side.cat].filter(Boolean).join(' · ')) + '</p>' +
          '<h3 class="mlsr-qv__title">' + esc(p.title) + '</h3>' +
          '<p class="mlsr-qv__price">' + money(p.price) + '<span> / unit</span></p>' +
          (side.deco ? '<p class="mlsr-qv__deco">' + esc(side.deco) + '</p>' : '') +
          (colors.length ? '<div class="mlsr-qv__block">' +
            '<p class="mlsr-qv__lbl">Colorways <span data-qv-colorname></span><em>' + colors.length + '</em></p>' +
            '<div class="mlsr-qv__ways">' + colors.map(function (c, i) {
              return '<button type="button" class="mlsr-qv__way" data-qv-color="' + esc(c) + '"' +
                (byColor[c] ? ' data-img="' + esc(byColor[c]) + '"' : '') +
                ' aria-pressed="' + (i === 0 ? 'true' : 'false') + '">' + esc(c) + '</button>';
            }).join('') + '</div></div>' : '') +
          (sizes.length ? '<div class="mlsr-qv__block"><p class="mlsr-qv__lbl">Sizes<em>' + sizes.length + '</em></p>' +
            '<div class="mlsr-qv__sizes">' + sizes.map(function (s) {
              return '<span class="mlsr-qv__size">' + esc(s) + '</span>';
            }).join('') + '</div></div>' : '') +
          (desc ? '<p class="mlsr-qv__desc">' + esc(desc) + '</p>' : '') +
          '<div class="mlsr-qv__foot">' +
            '<button type="button" class="mlsr-btn ' + (on ? 'mlsr-btn--outline' : 'mlsr-btn--teal') + ' mlsr-qv__cta" data-mlsr-pick="' + esc(p.handle) + '">' +
              (on ? ICO.tick + ' Added to your store' : ICO.add + ' Add product') + '</button>' +
            '<p class="mlsr-qv__fine">You choose colorways and print locations in the next step.</p>' +
          '</div>' +
        '</div>' +
      '</div>';
  };

  Picker.prototype.paintQuickView = function (handle) {
    var m = document.querySelector('[data-mlsr-qvmodal]');
    if (!m) return;
    var on = this.api.isSelected(handle);
    var cta = m.querySelector('.mlsr-qv__cta');
    if (!cta) return;
    cta.className = 'mlsr-btn ' + (on ? 'mlsr-btn--outline' : 'mlsr-btn--teal') + ' mlsr-qv__cta';
    cta.innerHTML = on ? ICO.tick + ' Added to your store' : ICO.add + ' Add product';
  };

  window.MLSRCatalog = {
    mount: function (host, api) { return new Picker(host, api); }
  };
})();
